import React from 'react';
import { Box, Typography } from '@mui/material';
import { SearchRounded } from '@mui/icons-material';
import { useAppTheme } from '../../theme';

interface CommandBarProps {
  /** Opens the EXISTING Ctrl/Cmd+K palette. This component never owns search. */
  onOpen: () => void;
}

/**
 * The global command affordance in the top bar.
 *
 * It looks like an input but is a BUTTON. There is exactly one search surface in
 * this app — the command palette in `src/command/CommandPalette.tsx`, which
 * already merges static navigation commands with live record search — and a
 * second real text field here would inevitably behave subtly differently
 * (different debounce, different result set, different Enter behaviour). Click
 * or Enter hands focus straight to the palette's own input instead.
 */
const CommandBar: React.FC<CommandBarProps> = ({ onOpen }) => {
  const { colors } = useAppTheme();

  // `navigator.platform` is deprecated but is still the only synchronous way to
  // tell a Mac from a PC; getting it wrong only mislabels a hint chip.
  const isMac = React.useMemo(
    () => typeof navigator !== 'undefined' && /mac/i.test(navigator.platform || ''),
    []
  );

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      aria-label="Ask Stratify anything — open the command palette"
      aria-keyshortcuts={isMac ? 'Meta+K' : 'Control+K'}
      sx={{
        display: { xs: 'none', md: 'flex' },
        alignItems: 'center',
        gap: 1.25,
        width: '100%',
        maxWidth: 520,
        px: 1.75,
        py: 0.875,
        borderRadius: 2.5,
        cursor: 'text',
        color: colors.textSecondary,
        backgroundColor: colors.background,
        border: `1px solid ${colors.border}`,
        transition: 'border-color .15s ease, background-color .15s ease, box-shadow .15s ease',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        '&:hover': {
          backgroundColor: colors.backgroundPaper,
          borderColor: colors.primary,
          boxShadow: `0 1px 2px rgba(15,23,42,0.06)`,
        },
        '&:focus-visible': {
          outline: `2px solid ${colors.primary}`,
          outlineOffset: 2,
        },
      }}
    >
      <SearchRounded sx={{ fontSize: 19, flexShrink: 0 }} />
      <Typography
        sx={{
          flexGrow: 1,
          fontSize: '0.8125rem',
          color: colors.textSecondary,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        Ask Stratify anything…
      </Typography>
      <Box
        component="kbd"
        aria-hidden="true"
        sx={{
          flexShrink: 0,
          fontFamily: 'inherit',
          fontSize: '0.6875rem',
          fontWeight: 600,
          letterSpacing: '0.02em',
          px: 0.75,
          py: 0.25,
          borderRadius: 1.25,
          color: colors.textSecondary,
          backgroundColor: colors.backgroundPaper,
          border: `1px solid ${colors.border}`,
          boxShadow: `0 1px 0 ${colors.border}`,
        }}
      >
        {isMac ? '⌘ K' : 'Ctrl K'}
      </Box>
    </Box>
  );
};

export default CommandBar;
